import ExprEval from 'expr-eval';
import { IServerSideGetRowsRequest } from '@ag-grid-community/core';

import { f, composeQuery, select } from 'common/soql_builder';
import { NamedSelect } from 'common/soql_builder/select';
import { ViewColumn } from 'common/types/viewColumn';
import SoqlHelpers from 'common/visualizations/helpers/SoqlHelpers';
import SoqlDataProvider from '../dataProviders/SoqlDataProvider';
import { Hierarchy, HierarchyColumnConfig } from '../vif';
import { unaliasRowData } from './TableDataQueryHelpers';
import { GrandTotalRowEntry, NetTotalRowEntry } from 'common/types/agGrid/grandTotal';
import { findCorrespondingColumn } from './TableDataColumnHelpers';

// Remove/simplify this entire file with `enable_prototype_net_total`
export async function setNetTotalDataQuery(
  soqlDataProvider: SoqlDataProvider,
  hierarchyConfig: Hierarchy,
  agRequest: IServerSideGetRowsRequest,
  displayableColumns: ViewColumn[],
  whereClause: any,
): Promise<GrandTotalRowEntry> {
  const configs = hierarchyConfig.columnConfigurations;
  const netTotalConfig = chooseNetTotalGroup(configs);
  const netTotalCalc = netTotalConfig?.netTotalCalc;
  const hasSumAgg = configs.some((col) => col.aggregation === 'sum');

  if (!netTotalCalc || !hasSumAgg) {
    return {};
  }

  const aliasedNetTotalRow: { [measureAlias: string]: string }[] = await soqlDataProvider.invokeSoqlQuery(
    netTotalQuery,
    [displayableColumns, hierarchyConfig, netTotalConfig, whereClause],
  );

  const netAggAliases = agRequest.valueCols
    .filter((col) => !!col.aggFunc && col.aggFunc === 'sum')
    .map((col, idx) => [col.id, SoqlHelpers.measureAlias(idx)])
    .reduce(
      (aliases, [colId, alias]) => {
        return { ...aliases, [alias]: colId };
      },
      { __group__: '__group__' },
    );

  const unaliasedNetTotalRow = unaliasRowData(aliasedNetTotalRow, { ...netAggAliases });

  // Here's some example data of where we're at:
  // [
  //   { '__group__': 'Tomatoes', 'total_sold': '100', 'total_produced': '101' },
  //   { '__group__': 'Oranges', 'total_sold': '200', 'total_produced': '501'}
  // ]

  // We compose it into something that's easier to pass into expr-eval.
  // Here, each inner object is in the same {key: value} format expected by expr-eval.evaluate(values)
  //  {
  //   'total_sold': { 'Tomatoes': '100', 'Oranges': '200' },
  //   'total_produced': { 'Tomatoes': '101', 'Oranges': '501' }
  // }
  const netTotalValues: { [columnName: string]: { [groupName: string]: string } } = {};
  unaliasedNetTotalRow.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (key === '__group__') {
        return;
      }
      if (!netTotalValues[key]) {
        netTotalValues[key] = {};
      }
      const groupName = netTotalFieldNormalize(row.__group__);
      const value = row[key];
      if (value) {
        netTotalValues[key][groupName] = value;
      }
    });
  });

  const finalNetTotalRow: NetTotalRowEntry = {};
  Object.keys(netTotalValues).forEach((columnName) => {
    const measureValues = netTotalValues[columnName];
    const result = parseCalc(netTotalCalc, measureValues);
    if (result !== null) {
      finalNetTotalRow[columnName] = result;
    }
  });

  // @ts-expect-error Numbers instead of strings is somewhat unsupported, this netTotal is a prototype feature
  return finalNetTotalRow;
}

const netTotalQuery = (
  columns: ViewColumn[],
  hierarchyConfig: Hierarchy,
  netTotalConfig: HierarchyColumnConfig,
  legacyWhereClause: string,
) => {
  const groupColumn = findCorrespondingColumn(columns, { id: netTotalConfig.columnName });

  const sumConfigs = hierarchyConfig.columnConfigurations.filter((col) => col.aggregation === 'sum');
  const sumColumns = sumConfigs.map((col) => findCorrespondingColumn(columns, { id: col.columnName }));
  const selectParts: NamedSelect[] = sumColumns.map((col, idx) => {
    return select(f.sum(col as ViewColumn), SoqlHelpers.measureAlias(idx));
  });

  selectParts.push(select(groupColumn as ViewColumn, '__group__'));

  const dataQuery = composeQuery({
    selects: selectParts,
    groups: [groupColumn],
    legacyWhereClause,
  });
  return dataQuery;
};

export const parseCalc = (fakeCalc: string, values: { [groupName: string]: string }): number | null => {
  try {
    const expr = ExprEval.Parser.parse(fakeCalc);

    // Check if all variables have a value (okay to have unused values)
    const variables = expr.variables();
    const unknownVars = variables.filter((variable) => !values.hasOwnProperty(variable));
    if (unknownVars.length > 0) {
      // Now we ought to mention that some values are unused
      const unusedValues = Object.keys(values).filter((value) => !variables.includes(value));
      console.error(
        `Unknown variables: ${unknownVars.join(', ')}
Unused variables: ${unusedValues.join(', ')}
Setting unknown vars to 0`,
      );

      const zeroVars = unknownVars.reduce((acc: Record<string, any>, zeroVar) => {
        acc[zeroVar] = 0;
        return acc;
      }, {});
      /**
       *
       * TODO: I don't think we have to update values here with the new zeroVars.
       * In fact, it's kind of an anti pattern to reassign function parameters.
       * Should just create a new variable for this.
       */
      values = { ...values, ...zeroVars };
    }

    const result = expr.evaluate(values);
    return result;
  } catch (e) {
    console.error(e);
    return null;
  }
};

/**
 * @param str - The string to normalize
 * @returns a snake_case string no other non-alphanumeric characters. Some fields are wrapped eg. _2019_
 */
export const netTotalFieldNormalize = (str: string): string => {
  const normalStr = str
    .toLowerCase()
    .replace(/[\s-]+/g, '_') // all spaces and dashes to underscores
    .replace(/[^a-z0-9_]/g, '') // everything else to nothing
    .replace(/_+/g, '_'); // Replace multiple underscores with a single underscore

  // expr-eval may fail to parse certain kinds of strings. Leading numbers will crash entirely.
  // We can wrap the string in underscores to make it parsable.
  // Instead of checking carefully, just test it and wrap if it crashes or there's no vars.
  // (yes I profiled this, its not expensive)
  try {
    const expr = ExprEval.Parser.parse(normalStr);
    if (expr.variables().length === 0) {
      // If it's just "2019", expr-eval won't think its a var.
      return `_${normalStr}_`;
    }
  } catch {
    return `_${normalStr}_`;
  }

  return normalStr;
};

export const chooseNetTotalGroup = (configs: HierarchyColumnConfig[]) => {
  // Grouping columns are always in order, we always want the first
  // This is an explicit limitation of this prototype, you can only net total the first group
  return configs.find((columnConfig) => columnConfig.isGrouping);
};
